/**
 * Two: Choose Services
 */
import React, { useState, useEffect, useRef, forwardRef } from 'react'
import styled from '@emotion/styled'
import PropTypes from 'prop-types'
import _, { groupBy, map, keyBy, sum } from 'lodash'
import { mq, trackViewServiceList, trackSelectService } from '../../../helpers'
 
import MarketService from './components/MarketService'
import SelectMarketServiceModal from './components/SelectMarketServiceModal'
import AddressModal from './components/AddressModal'
import LoadingStep from './components/LoadingStep'
 
import { useAllMarketServices } from '../../../services/GraphQlClientService'
import { gsColors } from '../../../styles/Variables'
 
import { Header2, Text, Header3 } from '../../../styles/Components'
 
const NewWebServiceSection = styled.section`
   width: 100%;
   display: flex;
   justify-content: center;
   align-items: flex-start;
 
   .service-container {
     width: 100%;
     max-width: 1263px;
     display: flex;
     flex-wrap: wrap;
     justify-content: space-between;
 
     ${mq('max', '1254px')} {
       max-width: 834px;
     }
 
     ${mq('max', '849px')} {
       display: none;
     }
   }
 `
 
const NewMobileServiceSection = styled.section`
   display: none;
 
   ${mq('max', '849px')} {
     display: flex;
     flex-direction: column;
     width: 100%;
   }
 `
 
const TwoHeader = styled.div`
   display: flex;
   flex-direction: column;
   align-items: center;
   width: 100%;
 
   ${mq('max', '768px')} {
     padding: 0 16px;
   }
 
   h2 {
     max-width: 800px;
 
     ${mq('max', '768px')} {
       font-size: 28px !important;
     }
   }
 
   p {
     max-width: 740px;
 
     ${mq('max', '768px')} {
       font-size: 14px !important;
       font-size: 14px;
       line-height: 20px;
       letter-spacing: 0.56px;
     }
   }
 `
 
const FilterSection = styled.div`
   display: none;
   flex-wrap: wrap;
 
   ${mq('max', '849px')} {
     margin: -2px 16px 40px;
     display: flex;
     justify-content: center;
     align-items: center;
     gap: 10px;
   }
 
   button {
     cursor: pointer;
     background-color: white;
     display: flex;
     padding: 5px 12px;
     justify-content: center;
     align-items: center;
     color: ${gsColors.meredithGrey};
     border-radius: 1px;
     border: 1px solid ${gsColors.lightTangerine};
     text-align: center;
     font-family: Euclid;
     font-size: 13px;
     font-weight: 400;
     line-height: 18px;
 
     &.selected {
       background-color: ${gsColors.sunsetOrange};
       color: #fff;
       border-color: ${gsColors.sunsetOrange};
     }
   }
 `
 
const ServiceTypeDiv = styled.div`
   width: 100%;
   max-width: 405px;
   margin-bottom: 111px;
 
   ${mq('max', '849px')} {
     max-width: initial;
     margin-bottom: 57px;
   }
 
   .service-title {
     font-family: Garnett !important;
 
     ${mq('max', '768px')} {
       font-size: 22px !important;
     }
   }
 
   .list-container {
     display: flex;
     flex-direction: column;
     align-items: center;
     gap: 24px;
 
     ${mq('max', '849px')} {
       gap: 20px;
     }
   }
 `
 
const Two = ({
  state: {
    address,
    market,
    serviceRequests = [],
    isDesktop,
    displayedMarketService,
    isMarketServiceModalOpen,
    scrollToServiceType,
  },
  dispatch,
  dispatchValueAtKeyPath,
  setIsStepValid,
  moveToNextStep,
  nextStep,
  selectMarketService,
  deselectMarketService,
}) => {
  // address modal
  const [isAddressModalOpen, setAddressModalOpen] = useState(false)
  const [serviceFilter, setServiceFilter] = useState('All')
  const openAddressModal = () => setAddressModalOpen(true)
  const closeAddressModal = () => setAddressModalOpen(false)
 
  const toggleMobileFilter = ({ target: { value } }) => setServiceFilter(value)
 
  // market service modal
  const setDisplayedMarketService = (displayedMarketService) =>
    dispatch({ displayedMarketService })
  const setMarketServiceModalOpen = (isMarketServiceModalOpen) =>
    dispatch({ isMarketServiceModalOpen })
 
  const openMarketServiceModal = (marketService) => {
    setDisplayedMarketService(marketService)
    setMarketServiceModalOpen(true)
 
    // track interaction w/ service
    trackSelectService(market, marketService)
  }
  const closeMarketServiceModal = () => setMarketServiceModalOpen(false)
 
  // close modal, dispatch updated address / market, move on to next step if no new market
  const onSubmitAddress = (updatedMarket, updatedAddress) => {
    closeAddressModal()
 
    // send event up to tag manager
    sendServicesAddedToGoogleTagManager()
 
    if (updatedMarket.id !== market.id) {
      // save address and new market to state
      dispatch({ address: updatedAddress, market: updatedMarket })
    } else {
      // save address to state and move to next step
      dispatch({ address: updatedAddress, step: nextStep })
    }
  }
 
  // load selected market and market services
  const allMarketServices = useAllMarketServices()
 
  // load available market services
  const marketServicesForMarket = _(allMarketServices)
    .filter((ms) => ms.marketId === market?.id)
    .sortBy([
      (ms) => ms.service.serviceType.priority,
      (ms) => ms.service.priority,
    ])
    .value()
 
  // track services display
  useEffect(() => {
    if (!marketServicesForMarket?.length) {
      return
    }
 
    trackViewServiceList(market, marketServicesForMarket)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify([marketServicesForMarket, market])])
 
  // handle scrolling to service type if provided
  const serviceTypeRef = useRef(null)
 
  if (serviceTypeRef.current) {
    serviceTypeRef.current.scrollIntoView({ scrollBehavior: 'smooth' })
  }
 
  if (!(market && allMarketServices)) {
    return <LoadingStep />
  }
 
  // group for display
  const marketServicesByServiceType = groupBy(
    marketServicesForMarket,
    'service.serviceType.name'
  )
 
  //sort by priority
  const sortedMarketServicesByServiceType = _.mapValues(
    marketServicesByServiceType,
    (ms) => _.sortBy(ms, 'service.priority')
  )
 
  const marketServicesByServiceId = keyBy(marketServicesForMarket, 'service.id')
 
  // in case of market change, filter out any selected services which don't exist here
  if (
    serviceRequests.find(
      ({ serviceId }) => !marketServicesByServiceId[serviceId]
    )
  ) {
    return dispatchValueAtKeyPath(
      'serviceRequests',
      serviceRequests.filter(
        ({ serviceId }) => marketServicesByServiceId[serviceId]
      )
    )
  }
 
  /**
    * sendServicesAddedToGoogleTagManager: sends event to google tag manager containing services in cart and total price
    *
    * @returns
    */
  const sendServicesAddedToGoogleTagManager = () => {
    if (!window.dataLayer) {
      return
    }
 
    let servicesAdded = serviceRequests.map(
      ({ serviceId }) => marketServicesByServiceId[serviceId]?.service?.name
    )
    let servicesSubtotal = sum(
      serviceRequests.map(
        ({ serviceId }) => marketServicesByServiceId[serviceId]?.price
      )
    )
 
    window.dataLayer.push({
      event: 'addServicesToCart',
      servicesAdded,
      servicesSubtotal,
    })
  }
 
  // step is valid if we have at least one service selected
  setIsStepValid(!!serviceRequests.length, 'Select Date & Time', () => {
    if (!address.zip) {
      return openAddressModal()
    }
 
    // send event up to tag manager
    sendServicesAddedToGoogleTagManager()
 
    return moveToNextStep()
  })
 
  return (
    <>
      <SelectMarketServiceModal
        isOpen={isMarketServiceModalOpen}
        close={closeMarketServiceModal}
        market={market}
        marketService={displayedMarketService}
        select={selectMarketService}
        deselect={deselectMarketService}
        isDesktop={isDesktop}
      />
      {/* if still required, collect the address from the user and move next */}
      <AddressModal
        currentMarket={market}
        isOpen={isAddressModalOpen}
        close={closeAddressModal}
        onSubmit={onSubmitAddress}
        isDesktop={isDesktop}
      />
      <TwoHeader>
        <Header2 margin="89px 0 16px" lineHeight="138%" align="center">
           Which services would you like to book?
        </Header2>
        <Text fontSize={18} lineHeight="26px" margin="0 0 32px">
           Make a selection to start. You&apos;ll be assigned one pro per
           service. Multiple services will be performed at once unless otherwise
           specified.
        </Text>
      </TwoHeader>
      <FilterSection>
        <button
          value="All"
          onClick={toggleMobileFilter}
          className={serviceFilter === 'All' ? 'selected' : ''}
        >
           All
        </button>
        {Object.keys(sortedMarketServicesByServiceType).map((t, i) => (
          <button
            className={serviceFilter === t ? 'selected' : ''}
            value={t}
            key={i}
            onClick={toggleMobileFilter}
          >
            {t}
          </button>
        ))}
      </FilterSection>
      <NewMobileServiceSection>
        {serviceFilter === 'All' ? (
          <>
            {marketServicesForMarket.filter((ms) => ms.isPopular).length ? (
              <ServiceTypeDiv>
                <Header3
                  className="service-title"
                  fontSize={22}
                  color={gsColors.cobalt}
                  weight={750}
                  margin="0 0 29px"
                >
                    Popular
                </Header3>
                <div className="list-container">
                  {marketServicesForMarket
                    .filter((ms) => ms.isPopular)
                    .map((marketService, i) => (
                      <MarketService
                        tabindex={i}
                        key={marketService.id}
                        marketService={marketService}
                        onClick={() => openMarketServiceModal(marketService)}
                        popular={true}
                      />
                    ))}
                </div>
              </ServiceTypeDiv>
            ) : null}
            {map(
              sortedMarketServicesByServiceType,
              (marketServices, serviceTypeName) => (
                <ServiceTypeDiv key={serviceTypeName}>
                  {serviceTypeName &&
                     scrollToServiceType &&
                     serviceTypeName.toLowerCase() ===
                       scrollToServiceType.toLowerCase() && (
                    <ScrollIntoView ref={serviceTypeRef} />
                  )}
                  <Header3
                    className="service-title"
                    fontSize={28}
                    align="left"
                    weight={750}
                    color={gsColors.cobalt}
                    lineHeight="138%"
                    margin="0 0 29px"
                  >
                    {serviceTypeName}
                  </Header3>
                  <div className="list-container">
                    {marketServices.map((marketService) => (
                      <MarketService
                        key={marketService.id}
                        marketService={marketService}
                        onClick={() => openMarketServiceModal(marketService)}
                        popular={isDesktop && marketService.isPopular}
                      />
                    ))}
                  </div>
                </ServiceTypeDiv>
              )
            )}
          </>
        ) : (
          <>
            <ServiceTypeDiv>
              {serviceFilter &&
                 scrollToServiceType &&
                 serviceFilter.toLowerCase() ===
                   scrollToServiceType.toLowerCase() && (
                <ScrollIntoView ref={serviceTypeRef} />
              )}
              <Header3
                className="service-title"
                fontSize={28}
                align="left"
                weight={750}
                color={gsColors.cobalt}
                lineHeight="138%"
                margin="0 0 36px"
              >
                {serviceFilter}
              </Header3>
              <div className="list-container">
                {sortedMarketServicesByServiceType[serviceFilter].map(
                  (marketService) => (
                    <MarketService
                      key={marketService.id}
                      marketService={marketService}
                      onClick={() => openMarketServiceModal(marketService)}
                      popular={isDesktop && marketService.isPopular}
                    />
                  )
                )}
              </div>
            </ServiceTypeDiv>
          </>
        )}
      </NewMobileServiceSection>
      <NewWebServiceSection>
        <div className="service-container">
          {map(
            sortedMarketServicesByServiceType,
            (marketServices, serviceTypeName) => (
              <ServiceTypeDiv key={serviceTypeName}>
                {serviceTypeName &&
                   scrollToServiceType &&
                   serviceTypeName.toLowerCase() ===
                     scrollToServiceType.toLowerCase() && (
                  <ScrollIntoView ref={serviceTypeRef} />
                )}
                <Header3
                  className="service-title"
                  fontSize={28}
                  align="center"
                  weight={750}
                  color={gsColors.cobalt}
                  lineHeight="138%"
                  margin="0 0 36px"
                >
                  {serviceTypeName}
                </Header3>
                <div className="list-container">
                  {marketServices.map((marketService) => (
                    <MarketService
                      key={marketService.id}
                      marketService={marketService}
                      onClick={() => openMarketServiceModal(marketService)}
                      popular={isDesktop && marketService.isPopular}
                    />
                  ))}
                </div>
              </ServiceTypeDiv>
            )
          )}
        </div>
      </NewWebServiceSection>
    </>
  )
}
 
Two.propTypes = {
  state: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  dispatchValueAtKeyPath: PropTypes.func.isRequired,
  setIsStepValid: PropTypes.func.isRequired,
  selectMarketService: PropTypes.func.isRequired,
}
 
export default Two
 
const ScrollIntoView = forwardRef((ignored, ref) => <div ref={ref}></div>)
 